import LoadingButton from '@mui/lab/LoadingButton';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { getAuth } from '../../providers/AppAuthProvider';
import { cancelStepUp, completeStepUp, getStepUpReason, onStepUpRequested } from '../../providers/stepUp';
import * as authService from '../../services/authService';

const GENERIC_ERROR = 'Could not verify, try again';

/** The service's `{code, message}` out of a failed call, and the `Retry-After` seconds of a 429. */
function describeFailure(err: unknown): { code?: string; message: string } {
  const response = (err as { response?: { status?: number; data?: { code?: unknown; message?: unknown }; headers?: any } })?.response;
  if (!response) return { message: GENERIC_ERROR };
  const code = typeof response.data?.code === 'string' ? response.data.code : undefined;
  const message = typeof response.data?.message === 'string' ? response.data.message : GENERIC_ERROR;
  const retryAfter = Number(response.headers?.['retry-after'] ?? response.headers?.get?.('retry-after'));
  if (response.status === 429 && Number.isFinite(retryAfter) && retryAfter > 0) {
    return { code, message: `${message}. Try again in ${retryAfter} seconds.` };
  }
  return { code, message };
}

/**
 * XBIT-4777: asks for an authenticator code when the service has refused a custody request with STEP_UP_REQUIRED, or
 * when a signing dialog calls `ensureStepUp()`. Mounted once in AdminLayout. Verify trades the code for a grant, which
 * `stepUp.ts` keeps in memory; Cancel resolves the waiting requests with null so they surface their original error.
 */
export default function StepUpDialog() {
  const [open, setOpen] = useState(false);
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [unavailable, setUnavailable] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [reason, setReason] = useState<string | null>(null);

  useEffect(
    () =>
      onStepUpRequested(() => {
        setOtp('');
        setError('');
        setUnavailable(false);
        setReason(getStepUpReason());
        setOpen(true);
      }),
    []
  );

  const cancel = useCallback(() => {
    setOpen(false);
    cancelStepUp();
  }, []);

  const submit = async () => {
    if (otp.length !== 6 || submitting) return;
    setError('');
    setSubmitting(true);
    try {
      const auth = await getAuth();
      if (!auth?.accessToken) throw new Error('No session');
      const answer = await authService.stepUp(otp, auth.accessToken);
      setOpen(false);
      completeStepUp({ stepUpToken: answer.stepUpToken, expiresIn: answer.expiresIn });
    } catch (err) {
      const failure = describeFailure(err);
      setError(failure.message);
      setUnavailable(failure.code === 'STEP_UP_UNAVAILABLE');
      setOtp('');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={cancel} maxWidth="xs" fullWidth>
      <DialogTitle>Confirm it&apos;s you</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
          Enter the 6-digit code from your authenticator app to continue.
        </Typography>
        {reason && (
          <Typography variant="body2" sx={{ mb: 2 }}>
            {reason}
          </Typography>
        )}
        <TextField
          fullWidth
          autoFocus
          label="Authenticator code"
          value={otp}
          disabled={unavailable}
          onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
          onKeyDown={(e) => {
            if (e.key === 'Enter') submit();
          }}
          error={!!error}
          helperText={error || ' '}
          inputProps={{ inputMode: 'numeric', autoComplete: 'one-time-code', maxLength: 6 }}
          InputLabelProps={{ shrink: true }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={cancel}>Cancel</Button>
        <LoadingButton variant="contained" color="inherit" loading={submitting} disabled={otp.length !== 6 || unavailable} onClick={submit}>
          Verify
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
